import React from 'react';
import Hammer from 'react-hammerjs';
import classNames from 'classnames';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import { ProductCard } from '@pes-ui/components-react';
import { EStatus } from '@pes-ui/utils/lib/constants';
import { BaseRouteProps } from '@pes-ui/utils/lib/types/route-props';

import { getProductCtas } from '../../utils/productCtas';
import { IconArrowLeft } from '../ui/IconArrowLeft';
import { IconArrowRight } from '../ui/IconArrowRight';
import { ProductsApi } from '@pes-ui/utils/lib/types/api/products-api';
import { ProductInfoApi } from '@pes-ui/utils/lib/types/api/products-info-api';
import { MappedProduct } from '../../utils/mappers';
import { goToCartUrl, isHostnameContainsCC } from '../../utils';
import { withNotificationStorageContext } from '../../hocs/NotificationStorage';
import { CommerceConnectorApi } from '@pes-ui/utils/lib/types/api/commerce-connector-api';

import styles from './index.module.scss';
import { TProductsRatings } from '@pes-ui/components/dist/collection/globals/types';
import { ProductAvailabilityApi } from '@pes-ui/utils/lib/types/api/product-availability-api';
import { ApcCtaApi } from '@pes-ui/utils/lib/types/api/apc-cta-api';
import { IconPlus } from '@pes-ui/components/dist/collection/components/UI/icon-plus/icon-plus';
import { getSingleDistributor } from '@pes-ui/components/dist/collection/components/commerce-connector/utils';
import { ProductsDistributorsInfo } from '@pes-ui/components/dist/collection/components/commerce-connector/types';
import { fetchers } from '@pes-ui/utils';
import { QdsStandaloneLink } from '@quartzds/core-react';

type Props = BaseRouteProps & {
    multipleProducts?: boolean;
    product: MappedProduct;
    productUIAAS: ProductsApi.Product;
    labels: ProductsApi.Labels;
    downloadButtonLabels: ProductInfoApi.DownloadButtonLabels;
    ccBuyOnlineAvailability: Record<string, boolean>;
    commerceConnector: CommerceConnectorApi.GetCommerceConnectorResponse;
    myProducts: string[];
    productsUIAASinfo: ProductInfoApi.GetProductsInfoResponse['productAdditionalInfos'];
    partnerPrice: string;
    onProductBuyOnlineClick: (e: CustomEvent) => void;
    onUpdateMyProductsList: () => void;
    onRemove: (productId: string) => void;
    startFrom: number;
    productsLength: number;
    onPrevProductChange: () => void;
    onNextProductChange: () => void;
    isMinified: boolean;
    productsRatings?: TProductsRatings;
    bazaarVoiceEnabled?: boolean;
    isAPCSite?: boolean;
    apcCta?: ApcCtaApi.ApcCtaResponse;
    productAvailabilityInfo?:ProductAvailabilityApi.ProductAvailabilityResponse;
    additionalDistributors?: ProductsDistributorsInfo;
    notification: {
        stack: [];
        addNotification: (params: {
            status: EStatus;
            message: React.ReactNode;
            buttonText?: string;
            countryCode?: string;
            languageCode?: string;
        }) => void;
        removeNotification: () => void;
    };
} & WrappedComponentProps;

class MobileProductsColumn extends React.Component<Props> {
    static defaultProps = {
        startFrom: 0,
        product: {},
        productsLength: 0,
        onPrevProductChange: () => {},
        onNextProductChange: () => {},
    };

    apcCtaButtonClick = event => {
        
        const {
            countryCode,
            languageCode,
        } = this.props;
        const { agentChatCscId } = this.props.commerceConnector.options;

         const { productId, bundle: { bundleId, qty} } = event.detail;
         const hostUrl = this.props?.apcCta?.urls?.apcHost;
         if (hostUrl) {
            const cartPageUrl = goToCartUrl(hostUrl, countryCode, languageCode, productId, bundleId, qty, agentChatCscId);
            window.open(cartPageUrl, '_self');
        }
    };

    onProductBuyOnlineClick = (event: CustomEvent) => {
        if (this.props.onProductBuyOnlineClick) {
            this.props.onProductBuyOnlineClick(event);
        }
    };

    isShowBuyOnline = (product): boolean => {
        return !!this.props.ccBuyOnlineAvailability[product.repositoryId] ||
            (!this.props.isAPCSite && this.props.additionalDistributors && !!this.props.additionalDistributors[product.repositoryId]);
    }

    onCtaClick = async (event) => {
        event.stopPropagation();

        const {
            detail: {
                type,
                product,
            }
        } = event;

        const {
            notification,
            contextRoot,
            countryCode,
            languageCode,
            myProducts,
            labels,
            onUpdateMyProductsList,
        } = this.props;

        const myProductsPayload={
            options: {
                allowPartialAdding: false
            },
            products: [{ productId: product.id, quantity: 1 }]
        }

        try {
            switch (type) {
                case 'cart':
                    await fetchers.postMyProducts({
                        contextRoot,
                        countryCode: !isHostnameContainsCC() ? countryCode : null,
                        languageCode,
                        payload: myProductsPayload
                    });

                    onUpdateMyProductsList();

                    notification.addNotification({
                        status: EStatus.WARNING,
                        message: this.props.intl.formatMessage({
                            id: 'labels.addedToCartLabel',
                            defaultMessage: labels.addedToCartLabel
                        }),
                        buttonText: this.props.intl.formatMessage({
                            id: 'labels.viewCartLabel',
                            defaultMessage: labels.viewCartLabel
                        }),
                        countryCode,
                        languageCode,
                    });
                    break;
                case 'favorites':
                    if (myProducts?.includes(product.id)) {
                        await fetchers.deleteMyProducts({
                            contextRoot,
                            countryCode: !isHostnameContainsCC() ? countryCode : null,
                            languageCode,
                            params: { id: product.id }
                        });

                        onUpdateMyProductsList();

                        notification.addNotification({
                            status: EStatus.WARNING,
                            message: this.props.intl.formatMessage({
                                id: 'labels.removedFromMyProductsLabel',
                                defaultMessage: labels.removedFromMyProductsLabel
                            }),
                            buttonText: '',
                        });
                    } else {
                        await fetchers.postMyProducts({
                            contextRoot,
                            countryCode: !isHostnameContainsCC() ? countryCode : null,
                            languageCode,
                            payload: myProductsPayload
                        });

                        onUpdateMyProductsList();

                        notification.addNotification({
                            status: EStatus.WARNING,
                            message: this.props.intl.formatMessage({
                                id: 'labels.addedToMyProductsLabel',
                                defaultMessage: labels.addedToMyProductsLabel
                            }),
                            buttonText: this.props.intl.formatMessage({
                                id: 'labels.viewMyProductsLabel',
                                defaultMessage: labels.viewMyProductsLabel
                            }),
                            countryCode,
                            languageCode,
                        });
                    }
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error(error);

            notification.addNotification({
                status: EStatus.ERROR,
                message: this.props.intl.formatMessage({
                    id:' comparison.errorMessage',
                    defaultMessage: 'You just added item to your Products'
                }),
                buttonText: '',
            });
        }
    };

    renderProduct = () => {
        const {
            languageCode,
            countryCode,
            bazaarVoiceEnabled,
            productsRatings,
            productUIAAS,
            productsUIAASinfo,
            onRemove,
            labels,
            isMinified,
            ccBuyOnlineAvailability,
            myProducts,
            commerceConnector,
            partnerPrice,
            isAPCSite,
            apcCta,
            productAvailabilityInfo,
            additionalDistributors,
            downloadButtonLabels,
        } = this.props;

        if (!productUIAAS || !productsUIAASinfo) return null;
        const { downloadButtonLabel, viewOnlineButtonLabel, seeSoftwareButtonLabel } = downloadButtonLabels;
        const productInfo = productsUIAASinfo.find((info) => info.productId === productUIAAS.repositoryId);
        const HOST = process.env.API_HOST || `${window.location.protocol}//${window.location.host}`;
        const height = { height: '100%' };
        const supplementedLabels = {
            ...labels,
            partnerPriceLabel: commerceConnector?.labels?.priceFrom
        };

        if (productUIAAS?.bundle) {
            productUIAAS.commercialReference = productUIAAS.repositoryId;
        }

        if (isMinified) {
            if (productUIAAS?.bundle) {
                const {
                    commercialReference,
                    pdpUrl: bundleUrl,
                    quantity,
                } = productUIAAS.bundle;
                const commonQuantity = 1 + quantity;
                const bundleItemsLabel = this.props?.labels?.bundleItems || 'Items:';

                return (
                    <>
                        <div className={styles.minifiedProductWrapper}>
                            <a href={productUIAAS.pdpUrl} className={styles.minifiedProductName}>
                                <div className={styles.countEllips}>x1</div>
                                {productUIAAS.commercialReference}
                            </a>
                            {IconPlus()}
                            <a href={bundleUrl} className={styles.minifiedProductName}>
                                <div className={styles.countEllips}>
                                    x{quantity}
                                </div>
                                {commercialReference}
                            </a>
                        </div>
                        <div className={styles.minifiedProductCount}>
                            <span>{`${bundleItemsLabel} ${commonQuantity}`}</span>
                        </div>
                    </>
                );
            }
            return (
                <QdsStandaloneLink
                  size='large' 
                  importance='standard'
                  href={ productUIAAS.pdpUrl }
                  className={ styles.minifiedProductName }
                  >
                    { productUIAAS.commercialReference }
                </QdsStandaloneLink>
            );
        }

        return (
            <ProductCard
                isMobile={true}
                style={height}
                hostWithoutLocaleCodes={HOST}
                className={styles.productCard}
                info={productInfo}
                showBuyOnline={!isMinified && this.isShowBuyOnline(productUIAAS)}
                isFocusShadowEnabled={false}
                withBorder={false}
                showClose={true}
                onCloseClick={onRemove}
                onBuyOnlineClick={this.onProductBuyOnlineClick}
                onCtaClick = {this.onCtaClick}
                onApcCtaButtonDidClick={this.apcCtaButtonClick}
                bazaarVoiceEnabled={bazaarVoiceEnabled}
                productsRatings={productsRatings}
                isAPCSite={isAPCSite}
                apcCta={apcCta}
                availabilityInfo={productAvailabilityInfo}
                productId={productUIAAS.repositoryId}
                languageCode={languageCode}
                countryCode={countryCode}
                product={productUIAAS}
                width='available'
                labels={supplementedLabels}
                downloadButtonLabel={downloadButtonLabel}
                viewOnlineButtonLabel={viewOnlineButtonLabel}
                seeSoftwareButtonLabel={seeSoftwareButtonLabel}
                isGridView={true}
                showHighlightedChar={true}
                showViewDetails={true}
                partnerPrice={partnerPrice}
                productCtas={getProductCtas({
                    labels: supplementedLabels,
                    myProducts,
                    commerceConnector
                })}
                singleDistributor={
                    !ccBuyOnlineAvailability[productUIAAS.repositoryId]
                        ? getSingleDistributor(additionalDistributors, productUIAAS.repositoryId)
                        : null
                }
            />
        );
    };

    renderProductInfo = () => {
        const {
            product,
            productsLength,
            startFrom,
            onPrevProductChange,
            onNextProductChange,
            isMinified,
            multipleProducts,
        } = this.props;
        const isProductSliderActive = productsLength > 2;

        const productCardWrapClass = classNames(styles.productCardWrap,
            multipleProducts && styles.productCardWrapMultiple, {
            [styles.productCardWrap_minified]: isMinified,
        });

        return (
            <div className={productCardWrapClass}>
                <div className={styles.innerWrap}>
                    {product ? this.renderProduct() : null}
                    {isProductSliderActive && (
                        <div className={styles.sliderInfoBar} dir='ltr'>
                            <button className={styles.sliderButton} onClick={onPrevProductChange}>
                                <IconArrowLeft width={10} height={10} />
                            </button>
                            <div className={styles.sliderInfo}>
                                {startFrom + 1}/{productsLength}
                            </div>
                            <button className={styles.sliderButton} onClick={onNextProductChange}>
                                <IconArrowRight width={10} height={10} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    render() {
        const { productsLength, onPrevProductChange, onNextProductChange } = this.props;
        const isProductSliderActive = productsLength > 2;

        return isProductSliderActive ? (
            <Hammer onSwipeLeft={onNextProductChange} onSwipeRight={onPrevProductChange}>
                {this.renderProductInfo()}
            </Hammer>
        ) : (
            this.renderProductInfo()
        );
    }
}

export default injectIntl(withNotificationStorageContext(MobileProductsColumn));
